import {CHART_CATEGORY_TYPE_RADIAL, getCategoryChartType, getChartType} from "../../components/chart/constants";
import {FILTER_DIM_SEPARATOR} from "./constants";
import {formatTemporalValue} from "../../utils/formatters";

export const formatChartValue = (value, type, timeFormatMap) => {
  if (value === null || value === undefined || value === "") {
    return value;
  }

  const format = timeFormatMap?.[type];
  if (!format) {
    return value;
  }

  const formattedValue = formatTemporalValue(value, type, format);
  if (formattedValue !== null) {
    return formattedValue;
  }

  return value;
};

export const formatChartFilterValue = (value, filterDims, dimensionTypeMap, timeFormatMap) => {
  const filterValues = String(value).split(FILTER_DIM_SEPARATOR);

  return filterDims
    .map((dim, idx) => formatChartValue(filterValues[idx], dimensionTypeMap?.[dim], timeFormatMap))
    .join(", ");
};

function matchesFilter(row, filter, filterDims) {
  const filterValues = String(filter).split(FILTER_DIM_SEPARATOR);
  return filterDims.every((dim, idx) => String(row[dim]) === filterValues[idx]);
}

export const getChartData = (
  type,
  data,
  primaryDim,
  secondaryDim,
  valueDims,
  filterDims,
  filter,
  dimensionValuesMap,
  labelMap,
  dimensionTypeMap,
  timeFormatMap
) => {
  const filteredData = filter ? data.filter(row => matchesFilter(row, filter, filterDims)) : data;

  const datasets = [];
  const chartCategory = getCategoryChartType(type);

  const primaryDimValues = primaryDim ? dimensionValuesMap[primaryDim] : [];
  const secondaryDimValues = secondaryDim ? dimensionValuesMap[secondaryDim] : [];

  if (chartCategory === CHART_CATEGORY_TYPE_RADIAL && (valueDims.length > 1 || secondaryDimValues.length > 1)) {
    if (secondaryDim) {
      primaryDimValues.forEach(primaryDimValue => {
        const filteredDataByPrimaryDim = filteredData.filter(row => matchesFilter(row, primaryDimValue, [primaryDim]));
        const data = [];
        valueDims.forEach(valueDim => {
          secondaryDimValues.forEach(secondaryDimValue => {
            const row = filteredDataByPrimaryDim.find(row => matchesFilter(row, secondaryDimValue, [secondaryDim]));
            data.push(row?.[valueDim] ?? null);
          });
        });
        datasets.push({
          type: getChartType(type),
          label: formatChartValue(primaryDimValue, dimensionTypeMap?.[primaryDim], timeFormatMap),
          data: data,
          dataAttributes: []
        });
      });
      const valuesInfo = [];
      valueDims.forEach(valueDim => {
        secondaryDimValues.forEach(secondaryDimValue => {
          valuesInfo.push({
            label: `${labelMap[valueDim]} - ${formatChartValue(secondaryDimValue, dimensionTypeMap?.[secondaryDim], timeFormatMap)}`
          });
        });
      });
      return {
        primaryDimInfo: {
          valuesInfo: valuesInfo
        },
        datasets: datasets
      };
    } else {
      primaryDimValues.forEach(primaryDimValue => {
        const row = filteredData.find(row => matchesFilter(row, primaryDimValue, [primaryDim]));
        datasets.push({
          type: getChartType(type),
          label: formatChartValue(primaryDimValue, dimensionTypeMap?.[primaryDim], timeFormatMap),
          data: valueDims.map(measure => row?.[measure] ?? null),
          dataAttributes: []
        });
      });
      return {
        primaryDimInfo: {
          valuesInfo: valueDims.map(measure => ({label: labelMap[measure]}))
        },
        datasets: datasets
      };
    }
  } else {
    valueDims.forEach(valueDim => {
      if (secondaryDim) {
        secondaryDimValues.forEach(secondaryDimValue => {
          const filteredDataBySecondaryDim = filteredData.filter(row =>
            matchesFilter(row, secondaryDimValue, [secondaryDim])
          );
          datasets.push({
            type: getChartType(type),
            label: `${labelMap[valueDim]} - ${formatChartValue(secondaryDimValue, dimensionTypeMap?.[secondaryDim], timeFormatMap)}`,
            data: primaryDimValues.map(primaryDimValue => {
              const row = filteredDataBySecondaryDim.find(row => matchesFilter(row, primaryDimValue, [primaryDim]));
              return row?.[valueDim] ?? null;
            }),
            dataAttributes: []
          });
        });
      } else {
        datasets.push({
          type: getChartType(type),
          label: labelMap[valueDim],
          data: primaryDimValues.map(primaryDimValue => {
            const row = filteredData.find(row => matchesFilter(row, primaryDimValue, [primaryDim]));
            return row?.[valueDim] ?? null;
          }),
          dataAttributes: []
        });
      }
    });
    return {
      primaryDimInfo: {
        label: labelMap[primaryDim],
        valuesInfo: primaryDimValues.map(primaryDimVal => ({
          label: formatChartValue(primaryDimVal, dimensionTypeMap?.[primaryDim], timeFormatMap)
        }))
      },
      datasets: datasets
    };
  }
};
